"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, CircleQuestionMark as HelpCircle, PhoneCall } from 'lucide-react';
export default function HomeAdditionFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const faqs = [{
    id: "permits-dekalb",
    question: "How are building permits and DeKalb County zoning handled in Brookhaven?",
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Because Brookhaven is an incorporated municipality within DeKalb County, residential additions require direct plan reviews through the <strong>City of Brookhaven Community Development Department</strong> rather than county offices. We coordinate every stage of this pipeline:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
            <li><strong>Zoning & Setback Verification:</strong> Confirming maximum lot coverage, buffer zones, and side/rear setbacks under local residential codes (R-100, R-85, or historic overlays).</li>
            <li><strong>Site & Arborist Plan:</strong> Producing tree canopy preservation plans and boundary surveys required by city arborists before grading begins.</li>
            <li><strong>Structural Plan Submission:</strong> Submitting stamped architectural and structural engineering plans directly for building, electrical, plumbing, and mechanical plan review.</li>
            <li><strong>Inspections:</strong> Scheduling and passing all phased rough-in, insulation, and final Certificate of Occupancy inspections.</li>
          </ul>
        </div>
  }, {
    id: "living-in-home",
    question: "Can we continue living in our Brookhaven home during construction?",
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            In most ground-level footprint additions (such as extending a primary suite, family room, or sunroom), homeowners comfortably remain in the home throughout the build. We enforce rigorous containment measures to protect your living environment:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
            <li><strong>ZipWall Air Barriers:</strong> Heavy-duty, zippered plastic containment walls and negative air HEPA filtration systems to isolate dust from existing living quarters.</li>
            <li><strong>Exterior Access:</strong> Framing crews and sub-contractors access the job site strictly via exterior breaches or temporary doors until late-phase trim integration.</li>
            <li><strong>Utility Cutover Management:</strong> Planned, short-duration shutoffs for plumbing or electrical connections, scheduled well in advance with minimal disruption.</li>
          </ul>
          <p className="text-xs text-foreground/80">
            <em>Note: For full second-story pop-top additions, we typically advise short-term alternative arrangements while the existing roof structure is open to weather.</em>
          </p>
        </div>
  }, {
    id: "timeline-planning",
    question: "What is the typical timeline for an addition from architectural design to move-in?",
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            A high-quality residential addition generally takes between <strong>5 to 9 months</strong>, broken down into two distinct phases:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">Pre-Construction (6–10 Weeks)</h4>
              <p className="text-xs text-muted-foreground">Architectural drafting, structural engineering, Brookhaven permit approval, and interior finishes selection.</p>
            </div>
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">Active Build (14–22 Weeks)</h4>
              <p className="text-xs text-muted-foreground">Excavation, foundation pours, framing, dry-in, MEP trades, insulation, drywall, and luxury interior finishing.</p>
            </div>
          </div>
          <p>
            We supply a live digital project schedule before construction starts so you always know upcoming trade milestones.
          </p>
        </div>
  }, {
    id: "change-orders",
    question: "How do you handle unexpected changes or homeowner design requests during the build?",
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Transparency is foundational to our process. We operate a zero-surprise policy regarding project scope and budgets:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
            <li><strong>Written Authorization:</strong> No modification is initiated without a written, digital Change Order detailing the exact scope adjustment, material cost, and timeline effect.</li>
            <li><strong>Client Portal Approval:</strong> Homeowners review and approve or decline changes directly through our builder management portal prior to work execution.</li>
            <li><strong>Unforeseen Condition Protocols:</strong> If hidden structural or utility discrepancies emerge when opening existing exterior walls, we provide real-time photographic documentation and fixed repair proposals before proceeding.</li>
          </ul>
        </div>
  }, {
    id: "foundation-clay",
    question: "What foundation considerations apply to North Georgia red clay and existing home tie-ins?",
    answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
          <p>
            Brookhaven's native Georgia red clay expands when wet and contracts when dry. Securing an addition requires careful geotechnical and structural tie-in engineering:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
            <li><strong>Soil Bearing & Footing Depth:</strong> We pour reinforced concrete continuous footings down to undisturbed load-bearing soil below the frost line, pinned with epoxy rebar anchors to your existing foundation walls.</li>
            <li><strong>Foundation Matching:</strong> Whether your home rests on a vented crawl space, stem wall, or poured concrete slab, we match floor system elevations with laser levels to help achieve step-free transitions.</li>
            <li><strong>Comprehensive Drainage & Waterproofing:</strong> Installation of dimpled exterior foundation membranes, perforated perimeter French drains, and positive site grading to direct rainwater completely away from the addition footprint.</li>
          </ul>
        </div>
  }];
  return <section id="homeadditionfaq" ref={ref} data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Context, Images, & Help Card */}
          <div className={`lg:col-span-5 space-y-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                <HelpCircle className="size-4" />
                <span>Brookhaven Home Addition Guide</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-[1.15]">
                Clear answers for your home addition.
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Expanding your Brookhaven home is a major investment. Here is how Brookhaven Elevation Renovations navigates local DeKalb codes, engineering, and craftsmanship.
              </p>
            </div>

            {/* Realistic Addition Visuals */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-lg overflow-hidden border border-border shadow-sm group aspect-[4/5]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_framing_exterior.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_framing_exterior.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_framing_exterior.webp-wvc-sizes" alt="Quality framing tie-in of a residential addition in Brookhaven Georgia" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent flex items-end p-3">
                  <span className="text-xs font-medium text-secondary-foreground">Structural Framing & Tie-Ins</span>
                </div>
              </div>

              <div className="relative rounded-lg overflow-hidden border border-border shadow-sm group aspect-[4/5]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_finished_interior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_finished_interior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_finished_interior.png-wvc-sizes" alt="Finished luxury master suite addition with vaulted ceiling and hardwood floors" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent flex items-end p-3">
                  <span className="text-xs font-medium text-secondary-foreground">Finished Living Space</span>
                </div>
              </div>
            </div>

            {/* Direct Contact Banner */}
            <div className="rounded-lg border border-border bg-card p-6 shadow-xs">
              <h3 className="font-serif font-semibold text-lg text-card-foreground">
                Have a unique zoning or property question?
              </h3>
              <p className="text-sm text-muted-foreground mt-2 mb-5">
                We review lot surveys, tree setbacks, and DeKalb County zoning requirements during our initial consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild variant="default" className="w-full sm:w-auto">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an Estimate
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/OurProjectsPage">
                    View Recent Additions
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion */}
          <div className={`lg:col-span-7 transition-all duration-700 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="bg-card rounded-xl border border-border p-6 sm:p-8 shadow-xs">
              <Accordion type="single" collapsible defaultValue="permits-dekalb" className="w-full space-y-2">
                {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-b border-border/80 last:border-b-0 py-1">
                    <AccordionTrigger className="text-left font-serif text-base sm:text-lg font-medium text-card-foreground hover:text-primary transition-colors py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-5 text-sm sm:text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            {/* Reassurance Footer Footnote */}
            <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground px-2">
              <span>Georgia General Contractor</span>
              <span>City of Brookhaven Compliant</span>
            </div>
          </div>

        </div>
      </div>
    </section>;
}